/**
 * Composable for PDF export functionality from OSMD
 * Handles SVG-to-PDF conversion using jsPDF and svg2pdf.js.
 *
 * Zwei Einstiegspunkte:
 * - `exportPdf(osmdInstance)`  – exportiert die SVGs einer BEREITS gerenderten (Live-)OSMD-Instanz
 *   (z. B. die interaktive Vorschau; enthält den aktuellen Zoom/Edit-Zustand).
 * - `exportPdfFromXml(xml)`    – rendert das MusicXML in einer TEMPORÄREN Offscreen-OSMD-Instanz und
 *   exportiert deren SVGs. Unabhängig davon, ob/wo gerade eine Vorschau gemountet ist.
 *
 * Die SVGs werden AUSSCHLIESSLICH aus dem OSMD-Container-DOM gelesen (`collectPageSvgs`) – NICHT
 * über die Interna `osmd.drawer.Backends`: die lösen in gebundelten osmd-extended-Builds nicht
 * zuverlässig auf und ließen den Export STILL scheitern (deshalb gibt es auch keinen
 * Backend-Fallback mehr).
 */

import jsPDF from 'jspdf';
import 'svg2pdf.js';

import {collectPageSvgs} from './osmd/pageSvgs';
import {ColoringModes, OpenSheetMusicDisplay} from 'opensheetmusicdisplay';
import {downloadBlob} from '@/services/download';
import {disableOsmdInternalInteraction} from '@/composables/useOsmdInteractiveScore';

// Strukturelle Sicht auf die OSMD-Interna, die der PDF-Export liest. In OpenSheetMusicDisplay sind
// container/sheet `protected`; hier wird bewusst strukturell darauf zugegriffen (kein `any`).
type OsmdInternals = {
  container?: HTMLElement;
  sheet?: { FullNameString?: string };
};

type SvgCapablePdf = jsPDF & {
  svg: (
    element: SVGElement,
    options: { x: number; y: number; width: number; height: number }
  ) => Promise<void>;
};

// Alle gerenderten `<svg>`-Seiten aus dem OSMD-Container-DOM (`collectPageSvgs`, geteilt mit dem
// Hit-Testing, das dieselbe Seitenliste für die Koordinaten-Umrechnung braucht).

/** Pixel-Größe eines SVG: viewBox → width/height-Attribut → getBoundingClientRect (robust). */
function svgSize(svg: SVGSVGElement): { w: number; h: number } {
  const vb = svg.viewBox?.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) {
    return {w: Math.round(vb.width), h: Math.round(vb.height)};
  }
  const w = svg.width?.baseVal?.value ?? 0;
  const h = svg.height?.baseVal?.value ?? 0;
  if (w > 0 && h > 0) return {w: Math.round(w), h: Math.round(h)};
  const rect = svg.getBoundingClientRect();
  return {w: Math.max(1, Math.round(rect.width)), h: Math.max(1, Math.round(rect.height))};
}

/** Gemeinsame PDF-Erzeugung: je SVG eine Seite in dessen eigener Pixel-Größe (keine Abhängigkeit von
 *  OSMDs PageFormat). Wirft, wenn keine SVGs vorliegen – der Aufrufer fängt + zeigt einen Fehler.
 *  Der Download läuft über den gemeinsamen `downloadBlob` (Anker mit `download`-Attribut) auf
 *  `pdf.output('blob')` (Blob-Typ `application/pdf`), NICHT über `pdf.save()` (dessen Fallback die
 *  Blob-URL nur in einem neuen Tab öffnete statt herunterzuladen). */
async function buildPdfFromSvgs(svgs: SVGSVGElement[], filename: string): Promise<void> {
  if (!svgs.length) {
    throw new Error('Keine gerenderte Partitur zum Exportieren gefunden.');
  }
  let pdf: jsPDF | null = null;
  for (const svg of svgs) {
    const {w, h} = svgSize(svg);
    const orient = h >= w ? 'p' : 'l';
    if (!pdf) {
      pdf = new jsPDF({orientation: orient, unit: 'px', format: [w, h]});
    } else {
      pdf.addPage([w, h], orient);
    }
    await (pdf as SvgCapablePdf).svg(svg, {x: 0, y: 0, width: w, height: h});
  }
  const blob = pdf!.output('blob');
  downloadBlob(blob, filename);
}

export function usePdfExport() {

  /**
   * Exportiert die SVGs einer bereits gerenderten (Live-)OSMD-Instanz als PDF (kein Server-Roundtrip).
   * Wirft bei fehlender Instanz/leerem Container – der Aufrufer soll den Fehler behandeln/anzeigen.
   */
  async function exportPdf(osmdInstance: OpenSheetMusicDisplay | null): Promise<void> {
    if (!osmdInstance) {
      throw new Error('Keine OSMD-Instanz für den PDF-Export vorhanden.');
    }
    const osmd = osmdInstance as unknown as OsmdInternals;
    const svgs = collectPageSvgs(osmd.container);
    const name = (osmd.sheet?.FullNameString ?? 'griffschrift').trim() || 'griffschrift';
    await buildPdfFromSvgs(svgs, `${name}.pdf`);
  }

  /**
   * Rendert `musicXml` in einer temporären Offscreen-OSMD-Instanz und exportiert das Ergebnis als PDF.
   * Unabhängig von einer sichtbaren Vorschau. Wirft bei leerem/ungültigem XML oder Render-/Export-Fehler.
   */
  async function exportPdfFromXml(musicXml: string, filename = 'griffschrift.pdf'): Promise<void> {
    if (!musicXml || !musicXml.trim()) {
      throw new Error('Kein MusicXML für den PDF-Export vorhanden.');
    }

    // Offscreen-Container mit echter Breite (0-Breite ⇒ OSMD überspringt das Rendern).
    const tmpDiv = document.createElement('div');
    tmpDiv.style.position = 'fixed';
    tmpDiv.style.left = '-100000px';
    tmpDiv.style.top = '0';
    tmpDiv.style.width = '1000px';
    document.body.appendChild(tmpDiv);

    let osmd: OpenSheetMusicDisplay | null = null;
    try {
      osmd = new OpenSheetMusicDisplay(tmpDiv, {
        backend: 'SVG',
        autoResize: false,
        performanceMode: true,
        // OOM-Gotcha: SkyBottomLine-Batch (getImageData auf großem Canvas) deaktivieren.
        skyBottomLineBatchMinMeasures: Number.MAX_SAFE_INTEGER,
        // Mehrseitiges, druckfreundliches A4-Hochformat für den Download.
        pageFormat: 'A4_P',
        newPageFromXML: false,
        newSystemFromXML: false,
        coloringEnabled: true,
        coloringMode: ColoringModes.XML,
        pageBackgroundColor: '#ffffff',
        drawTitle: true,
        drawSubtitle: true,
        drawComposer: true,
        drawLyricist: true,
        drawCredits: true,
        drawPartNames: true,
        drawMeasureNumbers: true,
        drawMetronomeMarks: true,
        drawLyrics: true,
      });
      // load() registriert sonst Scroll-Listener an scrollbaren Vorfahren (document-Ebene),
      // die nach dispose() verwaist crashen ("reading '0'") – abhängen vor dem ersten load().
      disableOsmdInternalInteraction(osmd);
      await osmd.load(musicXml);
      await osmd.render();
      await buildPdfFromSvgs(collectPageSvgs(tmpDiv), filename);
    } finally {
      try {
        osmd?.clear();
      } catch { /* ignore */ }
      try {
        osmd?.dispose();
      } catch { /* ignore */ }
      tmpDiv.remove();
    }
  }

  return {
    exportPdf,
    exportPdfFromXml,
  };
}
